import React, { useEffect, useMemo, useState } from 'react'
import { DatePicker, Popover, Segmented, Spin, Tooltip as AntTooltip, Typography } from 'antd'
import { QuestionCircleOutlined } from '@ant-design/icons'
import dayjs, { Dayjs } from 'dayjs'
import { Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts'
import { analyticsApi } from 'api/analytics'
import { IAnalyticsOrderPlaces } from 'models/analytics'
import { getDeclination } from 'shared/utils/getDeclination'
import { showServerError } from 'shared/utils/showServerError'
import styles from './Logistics.module.scss'
import 'dayjs/locale/ru'

type TDateRange = [Dayjs, Dayjs]

const PERIOD_FORMAT = 'YYYY-MM-DD'

const rangePresets: { label: string; value: TDateRange }[] = [
  { label: 'Год', value: [dayjs().subtract(1, 'year').add(1, 'day'), dayjs()] },
  { label: 'Полгода', value: [dayjs().subtract(6, 'month').add(1, 'day'), dayjs()] },
  { label: '90 дней', value: [dayjs().subtract(89, 'day'), dayjs()] },
  { label: '30 дней', value: [dayjs().subtract(29, 'day'), dayjs()] },
  { label: 'Этот год', value: [dayjs().startOf('year'), dayjs()] }
]

// Столько крупнейших мест получают свой сектор, остальные уходят в «Другой».
// Доля для отбора не годится: городов сотни, и выше любого разумного порога
// оказываются два-четыре
const TOP_PLACES = 10
// Подсказка над диаграммой не прокручивается, поэтому длинный список «Другого»
// в ней обрезан, а целиком он в легенде
const TOOLTIP_PLACES = 15

const placeColors = [
  '#2f75ff',
  '#FF9433',
  '#8b5cf6',
  '#22c55e',
  '#ef4444',
  '#06b6d4',
  '#eab308',
  '#ec4899',
  '#0f766e',
  '#a16207',
  '#475569'
]
const OTHER_COLOR = '#b8c2d1'

const PLACES_HINT =
  'Оплаченные супер-заказы, созданные за выбранный период, в том числе отменённые после ' +
  'оплаты. Место — куда доставляли заказ; если в корзине только самовывоз — где магазин ' +
  'продавца. Корзина с доставкой в разные города считается по первой части с доставкой. ' +
  'Свой сектор получают 10 крупнейших регионов или городов, остальные собраны в ' +
  '«Другой». Число заказов — при наведении на сектор. Москва и Московская область — ' +
  'разные регионы, как и Санкт-Петербург с Ленинградской областью.'

type TPlaceMode = 'region' | 'city'

const MODES: Record<TPlaceMode, { label: string; by: string; without: string }> = {
  region: { label: 'Регионы', by: 'региону', without: 'региона' },
  city: { label: 'Города', by: 'городу', without: 'города' }
}

interface IPlace {
  name: string
  orders: number
}

interface IPlaceShare extends IPlace {
  share: number
}

interface IPlaceSlice extends IPlaceShare {
  key: string
  fill: string
  // Места, собранные в «Другой»
  places?: IPlaceShare[]
}

const formatNumber = (value: number) => value.toLocaleString('ru-RU')

const formatShare = (share: number) => {
  const percent = share * 100
  return `${percent.toLocaleString('ru-RU', {
    maximumFractionDigits: percent < 10 ? 1 : 0
  })}%`
}

const formatOrders = (orders: number) =>
  `${formatNumber(orders)} ${getDeclination(orders % 100, ['заказов', 'заказ', 'заказа'])}`

const buildSlices = (places: IPlace[]): IPlaceSlice[] => {
  const total = places.reduce((sum, item) => sum + item.orders, 0)
  if (!total) {
    return []
  }

  const sorted: IPlaceShare[] = [...places]
    .sort((a, b) => b.orders - a.orders)
    .map((item) => ({ ...item, share: item.orders / total }))
  // «Другой» из одного места — то же место под чужим именем
  const isSplit = sorted.length > TOP_PLACES + 1
  const major = isSplit ? sorted.slice(0, TOP_PLACES) : sorted
  const minor = isSplit ? sorted.slice(TOP_PLACES) : []

  const slices: IPlaceSlice[] = major.map((item, index) => ({
    ...item,
    key: item.name,
    fill: placeColors[index] ?? OTHER_COLOR
  }))
  if (isSplit) {
    const orders = minor.reduce((sum, item) => sum + item.orders, 0)
    slices.push({
      key: 'other',
      name: 'Другой',
      orders,
      share: orders / total,
      fill: OTHER_COLOR,
      places: minor
    })
  }
  return slices
}

const PlaceList = ({ places, limit }: { places: IPlaceShare[]; limit?: number }) => {
  const shown = limit ? places.slice(0, limit) : places
  const rest = places.length - shown.length
  return (
    <>
      <ul className={limit ? styles.cityTooltipList : styles.cityList}>
        {shown.map((item) => (
          <li key={item.name}>
            <span>{item.name}</span>
            <span className={styles.muted}>
              {formatShare(item.share)} · {formatOrders(item.orders)}
            </span>
          </li>
        ))}
      </ul>
      {rest > 0 && (
        <span className={styles.muted}>
          и ещё {formatNumber(rest)} — весь список в легенде, у строки «Другой»
        </span>
      )}
    </>
  )
}

const SliceTooltip = ({ slice }: { slice?: IPlaceSlice }) =>
  slice ? (
    <div className={styles.chartTooltip}>
      <strong>
        {slice.name} — {formatShare(slice.share)}
      </strong>
      <span>{formatOrders(slice.orders)}</span>
      {slice.places && <PlaceList places={slice.places} limit={TOOLTIP_PLACES} />}
    </div>
  ) : null

export const OrderPlacesCard = () => {
  const [mode, setMode] = useState<TPlaceMode>('region')
  const [range, setRange] = useState<TDateRange>(rangePresets[0].value)
  const [data, setData] = useState<IAnalyticsOrderPlaces | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const from = range[0].format(PERIOD_FORMAT)
  const to = range[1].format(PERIOD_FORMAT)

  useEffect(() => {
    let isActual = true
    setIsLoading(true)

    analyticsApi
      .getOrderPlaces({ from, to })
      .then((response) => isActual && setData(response.data))
      .catch((e) => {
        if (!isActual) return
        setData(null)
        showServerError(e)
      })
      .finally(() => isActual && setIsLoading(false))

    return () => {
      isActual = false
    }
  }, [from, to])

  const unknown = mode === 'region' ? data?.regions_unknown : data?.cities_unknown

  const slices = useMemo(() => {
    const places =
      mode === 'region'
        ? (data?.regions ?? []).map((item) => ({ name: item.region, orders: item.paid_orders }))
        : (data?.cities ?? []).map((item) => ({ name: item.city, orders: item.paid_orders }))
    return buildSlices(places)
  }, [mode, data])

  const onRangeChange = (value: [Dayjs | null, Dayjs | null] | null) => {
    if (value?.[0] && value[1]) {
      setRange([value[0], value[1]])
    }
  }

  const renderBody = () => {
    if (!data) {
      return isLoading ? (
        <div className={styles.citiesChart} />
      ) : (
        <Typography.Text className={styles.muted}>Не удалось загрузить данные</Typography.Text>
      )
    }
    if (!slices.length) {
      return (
        <Typography.Text className={styles.muted}>
          {unknown
            ? `Ни у одного оплаченного заказа нет ${MODES[mode].without}`
            : 'Оплаченных заказов за период нет'}
        </Typography.Text>
      )
    }
    return (
      <div className={styles.cities}>
        <div className={styles.citiesChart}>
          <ResponsiveContainer width='100%' height='100%'>
            <PieChart>
              <Pie
                data={slices}
                dataKey='orders'
                nameKey='name'
                innerRadius='45%'
                outerRadius='95%'
                paddingAngle={1}
                isAnimationActive={false}
              />
              <Tooltip
                content={({ active, payload }) => (
                  <SliceTooltip
                    slice={
                      active
                        ? slices.find((slice) => slice.name === payload?.[0]?.name)
                        : undefined
                    }
                  />
                )}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <ul className={styles.cityLegend}>
          {slices.map((slice) => {
            const row = (
              <li key={slice.key} className={slice.places ? styles.cityLegendOther : undefined}>
                <span className={styles.cityDot} style={{ background: slice.fill }} />
                <span className={styles.cityName}>
                  {slice.name}
                  {slice.places && (
                    <span className={styles.muted}> ({formatNumber(slice.places.length)})</span>
                  )}
                </span>
                <strong>{formatShare(slice.share)}</strong>
                <span className={styles.muted}>{formatNumber(slice.orders)}</span>
              </li>
            )
            return slice.places ? (
              <Popover
                key={slice.key}
                placement='bottomLeft'
                trigger={['hover', 'click']}
                title={`${slice.name} — ${formatShare(slice.share)}, ${formatOrders(slice.orders)}`}
                content={
                  <div className={styles.cityPopover}>
                    <PlaceList places={slice.places} />
                  </div>
                }
              >
                {row}
              </Popover>
            ) : (
              row
            )
          })}
        </ul>
      </div>
    )
  }

  return (
    <div className={styles.card}>
      <div className={styles.cardHead}>
        <Typography.Title level={4} className={styles.cardTitle}>
          География заказов
          <AntTooltip title={PLACES_HINT}>
            <QuestionCircleOutlined className={styles.hint} />
          </AntTooltip>
        </Typography.Title>
        <div className={styles.cardControls}>
          <Segmented
            value={mode}
            options={(Object.keys(MODES) as TPlaceMode[]).map((value) => ({
              value,
              label: MODES[value].label
            }))}
            onChange={(value) => setMode(value as TPlaceMode)}
          />
          <DatePicker.RangePicker
            value={range}
            format='DD.MM.YYYY'
            presets={rangePresets}
            onChange={onRangeChange}
            disabledDate={(date) => date.isAfter(dayjs(), 'day')}
            allowClear={false}
            inputReadOnly
          />
        </div>
      </div>
      <Typography.Text className={styles.cardHint}>
        Оплаченные супер-заказы по {MODES[mode].by} покупателя
        {unknown ? `. Ещё ${formatOrders(unknown)} без ${MODES[mode].without}` : ''}
      </Typography.Text>
      <Spin spinning={isLoading}>{renderBody()}</Spin>
    </div>
  )
}
